import { useMutation, useQuery } from '@apollo/client/react'
import { deriveAccent, judgePair, readableForeground, STARTER_COLORS, SURFACES } from '@studeva/brand-contrast'
import { Loader, Upload, Wand2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import FilePickerButton from '~/components/FilePickerButton'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_APP_CONFIG, GQL_UPDATE_APP_CONFIG } from '~/gql/app-config/app-config'
import { STATUS_TEXT } from '~/helpers/branding/status-text'
import { readFileAsDataUri } from '~/helpers/files/read-file-as-data-uri'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useSyncOnChange } from '~/hooks/use-sync-on-change'
import { cn } from '~/lib/utils'
import { brandingSaveError } from './branding-save-error'

/**
 * Taille max d'un asset uploadé en data-URI (base64 stocké en DB). Le favicon
 * est bien plus bas que le logo : il part dans la query publique `appConfig`,
 * jouée avant login à chaque visite. Ces plafonds doublent ceux de l'API
 * (`branding-asset-limits.ts`), qui reste la source de vérité (#15).
 */
const MAX_ASSET_BYTES: Record<'logo' | 'logoDark' | 'favicon', number> = {
  logo: 512 * 1024,
  logoDark: 512 * 1024,
  favicon: 100 * 1024,
}

/** Valeurs de repli du thème, montrées dans l'aperçu tant que rien n'est choisi. */
const FALLBACK = { primary: '#F2B33D', accent: '#E5DAEB' } as const

type FormState = {
  appName: string
  primaryColor: string
  primaryForeground: string
  accentColor: string
  accentForeground: string
  footerText: string
  defaultLocale: string
  logo: string
  logoDark: string
  favicon: string
}

const EMPTY_FORM: FormState = {
  appName: '',
  primaryColor: '',
  primaryForeground: '',
  accentColor: '',
  accentForeground: '',
  footerText: '',
  defaultLocale: 'fr',
  logo: '',
  logoDark: '',
  favicon: '',
}

/**
 * Un champ couleur : la pastille native pour choisir, le champ texte pour coller
 * un hexadécimal de charte. Les deux sont présents parce que les deux usages
 * existent — on pioche à l'œil, ou on recopie une valeur imposée.
 */
function ColorField({
  id,
  label,
  hint,
  value,
  placeholder,
  onChange,
  children,
}: {
  id: string
  label: string
  hint?: string
  value: string
  placeholder: string
  onChange: (value: string) => void
  children?: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <input
          id={id}
          type="color"
          value={value || placeholder}
          onChange={e => onChange(e.target.value.toUpperCase())}
          className="size-9 shrink-0 cursor-pointer rounded border border-input bg-transparent"
          aria-label={label}
        />
        <Input
          // Le libellé visible vise le sélecteur de couleur ; ce champ saisit la même valeur en hexadécimal (RGAA 11.1).
          aria-label={`${label} (hex)`}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="max-w-40"
        />
        {children}
      </div>
    </div>
  )
}

function BrandingSettings() {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_APP_CONFIG, { fetchPolicy: 'network-only' })
  const [updateAppConfig, { loading: saving }] = useMutation(GQL_UPDATE_APP_CONFIG)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)

  useSyncOnChange(data?.appConfig, cfg => setForm({
    appName: cfg.appName ?? '',
    primaryColor: cfg.primaryColor ?? '',
    primaryForeground: cfg.primaryForeground ?? '',
    accentColor: cfg.accentColor ?? '',
    accentForeground: cfg.accentForeground ?? '',
    footerText: cfg.footerText ?? '',
    defaultLocale: cfg.defaultLocale ?? 'fr',
    logo: cfg.logo ?? '',
    logoDark: cfg.logoDark ?? '',
    favicon: cfg.favicon ?? '',
  }))

  const set = (key: keyof FormState) => (value: string) =>
    setForm(prev => ({ ...prev, [key]: value }))

  /*
   * Les quatre couleurs sont SAISIES, aucune n'est déduite d'une autre.
   *
   * L'accent était dérivé de la primaire : cohérent, mais ce n'était pas la
   * couleur de l'organisation, qui en a presque toujours une seconde dans sa
   * charte. Même chose pour les encres — une marque impose souvent le blanc
   * cassé ou le noir de son logo, que le calcul remplaçait par du #FFFFFF.
   *
   * Ce que le calcul garde : un point de DÉPART, proposé sur demande.
   */
  const effective = {
    primary: form.primaryColor || FALLBACK.primary,
    accent: form.accentColor || FALLBACK.accent,
  }
  const suggested = {
    primaryForeground: readableForeground(effective.primary) ?? '#FFFFFF',
    accentForeground: readableForeground(effective.accent) ?? '#111111',
  }
  const ink = {
    primary: form.primaryForeground || suggested.primaryForeground,
    accent: form.accentForeground || suggested.accentForeground,
  }

  // Le verdict porte sur les DEUX thèmes : un couple lisible sur fond blanc
  // peut disparaître sur fond noir, et l'apprenant choisit son thème.
  const couples = [
    { key: 'primary', label: t('branding.primaryColor'), bg: effective.primary, fg: ink.primary },
    { key: 'accent', label: t('branding.accentColor'), bg: effective.accent, fg: ink.accent },
  ] as const
  const themes = [
    { key: 'light', surface: SURFACES.light, ink: '#111111', ok: STATUS_TEXT.light.success, fail: STATUS_TEXT.light.warning, label: t('theme.light') },
    { key: 'dark', surface: SURFACES.dark, ink: '#F5F5F5', ok: STATUS_TEXT.dark.success, fail: STATUS_TEXT.dark.warning, label: t('theme.dark') },
  ] as const

  const handleFile = (key: 'logo' | 'logoDark' | 'favicon') => async (file: File) => {
    const max = MAX_ASSET_BYTES[key]
    if (file.size > max) {
      toast.error(t('branding.imageTooLarge', { max: Math.round(max / 1024) }))
      return
    }
    try {
      const dataUri = await readFileAsDataUri(file)
      set(key)(dataUri)
    } catch {
      toast.error(t('branding.readError'))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await updateAppConfig({
        variables: {
          // Les champs vides partent en chaîne vide et NON en `undefined` :
          // l'API lit la première comme un effacement, la seconde comme un
          // champ non fourni. Sans ça, le bouton « Supprimer » ne supprimait
          // rien — l'ancien asset restait en base sans rien dire (#31).
          input: {
            appName: form.appName || undefined,
            primaryColor: form.primaryColor,
            primaryForeground: form.primaryForeground,
            accentColor: form.accentColor,
            accentForeground: form.accentForeground,
            footerText: form.footerText,
            defaultLocale: form.defaultLocale || undefined,
            logo: form.logo,
            logoDark: form.logoDark,
            favicon: form.favicon,
          },
        },
      })
      await refetch()
      toast.success(t('branding.saved'))
    } catch (error) {
      const cause = brandingSaveError(error)
      if (cause.kind === 'assetTooLarge') {
        toast.error(t('branding.assetTooLarge', {
          asset: t(`branding.${cause.asset}`),
          max: Math.round(MAX_ASSET_BYTES[cause.asset] / 1024),
        }))
      } else if (cause.kind === 'inkTooLow') {
        toast.error(t('branding.inkTooLow', { color: t(`branding.${cause.color}Color`) }))
      } else {
        toast.error(t('branding.saveError'))
      }
    }
  }

  /** Une case d'upload de logo, avec son aperçu sur le fond qui la concerne. */
  const logoSlot = (key: 'logo' | 'logoDark', label: string, surface: string) => (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium">{label}</span>
      <div
        className="flex h-16 items-center justify-center rounded border border-border px-3"
        style={{ backgroundColor: surface }}
      >
        {form[key]
          ? <img src={form[key]} alt={label} className="max-h-10 w-auto object-contain" />
          : (
              <span className="text-xs" style={{ color: surface === SURFACES.light ? '#737373' : '#A3A3A3' }}>
                {t('branding.noLogo')}
              </span>
            )}
      </div>
      <div className="flex items-center gap-1">
        <FilePickerButton accept="image/*" onFile={handleFile(key)} aria-label={`${t('common.choose')} — ${label}`}>
          <Upload className="mr-2 size-4" />
          {t('common.choose')}
        </FilePickerButton>
        {form[key] && (
          <Button type="button" variant="ghost" size="sm" onClick={() => set(key)('')}>
            {t('common.remove')}
          </Button>
        )}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner className="size-6" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6">
      <Card {...tourAnchor('branding.identity')}>
        <CardHeader>
          <CardTitle>{t('branding.identity')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="appName">{t('branding.appName')}</Label>
            <Input
              id="appName"
              value={form.appName}
              onChange={e => set('appName')(e.target.value)}
              placeholder="Studeva"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('branding.logo')}</Label>
            {/*
                Contraintes annoncées AVANT le choix du fichier, pas après un
                refus : l'organisme les découvrait en se faisant rejeter, une
                fois le fichier déjà préparé.
              */}
            <p className="text-xs text-muted-foreground">
              {t('branding.logo.constraints', { max: Math.round(MAX_ASSET_BYTES.logo / 1024) })}
            </p>
            {/*
                Deux emplacements, chacun présenté sur SON fond (#31). Un seul
                fichier obligeait le logo monochrome à choisir son camp : posé
                sur le thème qu'il n'aime pas, il disparaît, et l'organisation
                ne le voit jamais puisqu'elle travaille dans son thème à elle.
                Le second reste facultatif — un logo bicolore tient sur les deux.
              */}
            <div className="grid gap-4 sm:grid-cols-2">
              {logoSlot('logo', t('branding.logo.light'), SURFACES.light)}
              {logoSlot('logoDark', t('branding.logo.dark'), SURFACES.dark)}
            </div>
            <p className="text-xs text-muted-foreground">{t('branding.logo.darkHint')}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('branding.favicon')}</Label>
            <p className="text-xs text-muted-foreground">
              {t('branding.favicon.constraints', { max: Math.round(MAX_ASSET_BYTES.favicon / 1024) })}
            </p>
            <div className="flex items-center gap-4">
              {form.favicon
                ? <img src={form.favicon} alt="favicon" className="size-8 rounded border border-border object-contain" />
                : <span className="text-sm text-muted-foreground">{t('branding.noFavicon')}</span>}
              <FilePickerButton accept="image/*" onFile={handleFile('favicon')} aria-label={`${t('common.choose')} — ${t('branding.favicon')}`}>
                <Upload className="mr-2 size-4" />
                {t('common.choose')}
              </FilePickerButton>
              {form.favicon && (
                <Button type="button" variant="ghost" size="sm" onClick={() => set('favicon')('')}>
                  {t('common.remove')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card {...tourAnchor('branding.colors')}>
        <CardHeader>
          <CardTitle>{t('branding.colorsTypography')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <ColorField
            id="primaryColor"
            label={t('branding.primaryColor')}
            value={form.primaryColor}
            placeholder={FALLBACK.primary}
            onChange={set('primaryColor')}
          />

          {/*
              Palette de départ : c'est devant un sélecteur vide qu'on choisit
              mal. Ces six teintes passent AA sur les deux thèmes — le test
              `contrast.spec.ts` le vérifie, sans quoi proposer une palette
              donnerait juste l'illusion d'un choix sûr.
            */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">{t('branding.starter')}</span>
            {STARTER_COLORS.map(color => (
              <button
                key={color}
                type="button"
                onClick={() => set('primaryColor')(color)}
                aria-label={color}
                className={cn(
                  'size-6 rounded-full border transition-transform hover:scale-110',
                  form.primaryColor.toUpperCase() === color ? 'border-foreground' : 'border-border',
                )}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>

          <ColorField
            id="primaryForeground"
            label={t('branding.primaryForeground')}
            hint={t('branding.foreground.hint')}
            value={form.primaryForeground}
            placeholder={suggested.primaryForeground}
            onChange={set('primaryForeground')}
          >
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => set('primaryForeground')(suggested.primaryForeground)}
            >
              <Wand2 className="mr-2 size-4" />
              {t('branding.suggest')}
            </Button>
          </ColorField>

          <ColorField
            id="accentColor"
            label={t('branding.accentColor')}
            hint={t('branding.accentColor.hint')}
            value={form.accentColor}
            placeholder={FALLBACK.accent}
            onChange={set('accentColor')}
          >
            {/*
                La dérivation devient une PROPOSITION : l'organisme qui a une
                seconde couleur la saisit, celui qui n'en a pas évite la page
                blanche. C'est le choix qui doit rester à lui, pas le calcul.
              */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => set('accentColor')(deriveAccent(effective.primary) ?? '')}
            >
              <Wand2 className="mr-2 size-4" />
              {t('branding.accent.fromPrimary')}
            </Button>
          </ColorField>

          <ColorField
            id="accentForeground"
            label={t('branding.accentForeground')}
            hint={t('branding.foreground.hint')}
            value={form.accentForeground}
            placeholder={suggested.accentForeground}
            onChange={set('accentForeground')}
          >
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => set('accentForeground')(suggested.accentForeground)}
            >
              <Wand2 className="mr-2 size-4" />
              {t('branding.suggest')}
            </Button>
          </ColorField>

          {/*
              Le simulateur juge les COUPLES réellement choisis, et les montre.
              Il ne notait auparavant qu'une couleur seule, ce qui laissait
              passer le cas le plus courant : un fond parfaitement visible dont
              le libellé, lui, est illisible. Deux verdicts distincts, donc —
              le texte se lit-il sur le fond, le fond se détache-t-il de la page.

              Le texte illisible est refusé par l'API (#99), le fond qui se
              confond avec la page reste un avertissement : le dire ici, avant
              l'enregistrement, évite un refus incompris.
            */}
          <div className="flex flex-col gap-3">
            <span className="text-xs text-muted-foreground">{t('branding.contrast.title')}</span>
            <div className="grid gap-2 sm:grid-cols-2">
              {themes.map(theme => (
                <div
                  key={theme.key}
                  className="flex flex-col gap-3 rounded-lg border border-border p-3"
                  style={{ backgroundColor: theme.surface, color: theme.ink }}
                >
                  <span className="text-xs font-medium opacity-70">{theme.label}</span>
                  {couples.map((couple) => {
                    const verdict = judgePair(couple.bg, couple.fg, theme.surface)
                    if (!verdict) {
                      return (
                        <span key={couple.key} className="text-xs opacity-70">
                          {t('branding.contrast.invalid', { label: couple.label })}
                        </span>
                      )
                    }
                    return (
                      <div key={couple.key} className="flex flex-col gap-1">
                        {/* L'échantillon porte les couleurs SAISIES : c'est
                              ce que l'organisme veut voir, pas un chiffre. */}
                        <span
                          className="inline-flex w-fit items-center rounded-md px-3 py-1.5 text-sm font-medium"
                          style={{ backgroundColor: couple.bg, color: couple.fg }}
                        >
                          {couple.label}
                        </span>
                        {/* Le verdict est posé sur l'échantillon, pas sur la page : ses
                              couleurs sont celles de l'échantillon, AA (RGAA 3.2). */}
                        <span className="text-xs tabular-nums">
                          {t('branding.contrast.text', { ratio: verdict.textRatio.toFixed(1) })}
                          {' · '}
                          <span style={{ color: verdict.textReadable ? theme.ok : theme.fail }}>
                            {verdict.textReadable ? t('branding.contrast.textOk') : t('branding.contrast.textFail')}
                          </span>
                        </span>
                        <span className="text-xs tabular-nums">
                          {t('branding.contrast.surface', { ratio: verdict.surfaceRatio.toFixed(1) })}
                          {' · '}
                          <span style={{ color: verdict.surfaceVisible ? theme.ok : theme.fail }}>
                            {verdict.surfaceVisible ? t('branding.contrast.surfaceOk') : t('branding.contrast.surfaceFail')}
                          </span>
                        </span>
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>

          {/*
              Prévisualisation du rendu APPLIQUÉ, avant enregistrement.
              L'organisme jugeait jusqu'ici sa couleur sur une pastille de six
              pixels ; ce qu'il veut savoir, c'est à quoi ressemble un bouton —
              et lequel de ses deux logos s'affiche sur ce fond-là.
            */}
          <div className="flex flex-col gap-2">
            <span className="text-xs text-muted-foreground">{t('branding.preview')}</span>
            <div className="grid gap-2 sm:grid-cols-2">
              {themes.map(theme => (
                <div
                  key={theme.key}
                  className="flex flex-col gap-3 rounded-lg border border-border p-4"
                  style={{ backgroundColor: theme.surface, color: theme.ink }}
                >
                  <div className="flex items-center gap-2">
                    {/* Le logo montré est celui que CE thème affichera, avec
                          le même repli que `BrandLogo` : une seule variante
                          fournie sert des deux côtés. */}
                    {(theme.key === 'dark' ? form.logoDark || form.logo : form.logo || form.logoDark)
                      ? (
                          <img
                            src={theme.key === 'dark' ? form.logoDark || form.logo : form.logo || form.logoDark}
                            alt=""
                            className="h-6 w-auto object-contain"
                          />
                        )
                      : <span className="font-heading text-sm font-semibold">{form.appName || 'Studeva'}</span>}
                  </div>
                  <span
                    className="inline-flex w-fit items-center rounded-md px-3 py-1.5 text-sm font-medium"
                    style={{ backgroundColor: effective.primary, color: ink.primary }}
                  >
                    {t('branding.preview.button')}
                  </span>
                  <span
                    className="rounded px-2 py-1 text-xs"
                    style={{ backgroundColor: effective.accent, color: ink.accent }}
                  >
                    {t('branding.preview.hover')}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/*
              Plus de choix de police (#31). Un champ libre ne garantissait pas
              que la famille saisie soit chargée : le navigateur retombait en
              silence sur autre chose, et l'organisme croyait avoir changé sa
              typographie. La typographie est celle du produit, définie une
              fois dans le thème.
            */}
          <p className="text-xs text-muted-foreground">{t('branding.typography.fixed')}</p>
        </CardContent>
      </Card>

      <Card {...tourAnchor('branding.misc')}>
        <CardHeader>
          <CardTitle>{t('branding.misc')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="footerText">{t('branding.footerText')}</Label>
            <Input
              id="footerText"
              value={form.footerText}
              onChange={e => set('footerText')(e.target.value)}
              placeholder={t('branding.footerPlaceholder')}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="defaultLocale">{t('branding.defaultLocale')}</Label>
            <select
              id="defaultLocale"
              value={form.defaultLocale}
              onChange={e => set('defaultLocale')(e.target.value)}
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="fr">{t('common.french')}</option>
              <option value="en">{t('common.english')}</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving} {...tourAnchor('branding.save')}>
          {saving && <Loader className="mr-2 size-4 animate-spin" />}
          {t('common.save')}
        </Button>
      </div>
    </form>
  )
}

export default BrandingSettings
